import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadIcon, CheckCircle2Icon, Loader2Icon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
const US_STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
const SERVICE_TYPES = ['New Installation', 'Maintenance Request', 'Design Consultation', 'Annual Program', 'Other'];
interface InquiryFormProps {
  variant: 'maintenance' | 'quote';
}
export function InquiryForm({
  variant
}: InquiryFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [worksheetOpen, setWorksheetOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    propertyName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    primaryName: '',
    primaryPhone: '',
    primaryEmail: '',
    secondaryName: '',
    secondaryPhone: '',
    serviceType: '',
    description: '',
    agreement: false
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size exceeds 2MB limit.');
        if (fileInputRef.current) fileInputRef.current.value = '';
        setFileName(null);
        return;
      }
      setFileName(file.name);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      toast.error('Please provide a description of your needs.');
      return;
    }
    if (!formData.agreement) {
      toast.error('You must agree to the terms of service to submit.');
      return;
    }
    setIsSubmitting(true);
    try {
      let fileUrl = '';
      // Upload file to temporary hosting to get a download link
      if (fileInputRef.current?.files?.[0]) {
        const file = fileInputRef.current.files[0];
        const fileFormData = new FormData();
        fileFormData.append('file', file);
        try {
          const uploadResponse = await fetch('https://tmpfiles.org/api/v1/upload', {
            method: 'POST',
            body: fileFormData
          });
          const uploadResult = await uploadResponse.json();
          if (uploadResult.status === 'success' && uploadResult.data?.url) {
            fileUrl = uploadResult.data.url.replace('tmpfiles.org/', 'tmpfiles.org/dl/');
          }
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          toast.error('File upload failed. Submitting request without file.');
        }
      }
      const payload: Record<string, string> = {};
      Object.entries(formData).forEach(([key, value]) => {
        payload[key] = String(value);
      });
      // Add variant to payload
      payload['formVariant'] = variant;
      if (fileUrl) {
        payload['fileUrl'] = fileUrl;
        payload['fileName'] = fileName || '';
      }
      const response = await fetch('https://services.leadconnectorhq.com/hooks/O6PUtNjwO7XTD0gs7XsU/webhook-trigger/bba98d68-f409-46ca-9920-6d317ac9c7d2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error('Failed to submit request');
      }
      toast.success('Request submitted successfully! A commercial team member will contact you within 24 hours.');
    } catch (error) {
      toast.error('Something went wrong. Please try again or contact us directly.');
      setIsSubmitting(false);
      return;
    }
    // Reset form
    setFormData({
      date: '',
      time: '',
      propertyName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      primaryName: '',
      primaryPhone: '',
      primaryEmail: '',
      secondaryName: '',
      secondaryPhone: '',
      serviceType: '',
      description: '',
      agreement: false
    });
    setFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setIsSubmitting(false);
  };
  const inputClasses = 'w-full min-w-0 bg-[#111111] border border-white/10 rounded-sm px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-tcp-red focus:ring-1 focus:ring-tcp-red transition-colors box-border';
  const labelClasses = 'block text-sm font-medium text-tcp-gray mb-2';
  const sectionHeaderClasses = 'font-serif text-xl text-white border-b border-white/10 pb-2 mb-6 mt-10 first:mt-0';
  const submitButtonText = variant === 'maintenance' ? 'Submit Maintenance Request' : 'Submit Quote Request';
  return <div className="max-w-4xl mx-auto" data-id="element-35">
      <motion.form initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} onSubmit={handleSubmit} className="bg-tcp-darkgray border border-white/5 rounded-md p-6 md:p-10 shadow-2xl overflow-hidden" data-id="element-36">
        {/* Section A */}
        <h3 className={sectionHeaderClasses} data-id="element-37">Property Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-38">
          <div data-id="element-39">
            <label className={labelClasses} data-id="element-40">Date of Request</label>
            <input type="date" name="date" value={formData.date} onChange={handleChange} className={inputClasses} style={{
            colorScheme: 'dark'
          }} data-id="element-41" />
          </div>
          <div data-id="element-42">
            <label className={labelClasses} data-id="element-43">Time of Request</label>
            <input type="time" name="time" value={formData.time} onChange={handleChange} className={inputClasses} style={{
            colorScheme: 'dark'
          }} data-id="element-44" />
          </div>
          <div className="md:col-span-2" data-id="element-45">
            <label className={labelClasses} data-id="element-46">Property Name</label>
            <input type="text" name="propertyName" value={formData.propertyName} onChange={handleChange} className={inputClasses} placeholder="e.g. The Grand Hotel" data-id="element-47" />
          </div>
          <div className="md:col-span-2" data-id="element-48">
            <label className={labelClasses} data-id="element-49">Property Address</label>
            <input type="text" name="address" value={formData.address} onChange={handleChange} className={inputClasses} placeholder="123 Main St" data-id="element-50" />
          </div>
          <div data-id="element-51">
            <label className={labelClasses} data-id="element-52">City</label>
            <input type="text" name="city" value={formData.city} onChange={handleChange} className={inputClasses} data-id="element-53" />
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-54">
            <div data-id="element-55">
              <label className={labelClasses} data-id="element-56">State</label>
              <select name="state" value={formData.state} onChange={handleChange} className={inputClasses} data-id="element-57">
                <option value="" data-id="element-58">Select...</option>
                {US_STATES.map(state => <option key={state} value={state} data-id="element-59">
                    {state}
                  </option>)}
              </select>
            </div>
            <div data-id="element-60">
              <label className={labelClasses} data-id="element-61">Zip Code</label>
              <input type="text" name="zip" value={formData.zip} onChange={handleChange} className={inputClasses} data-id="element-62" />
            </div>
          </div>
        </div>

        {/* Section B */}
        <h3 className={sectionHeaderClasses} data-id="element-63">
          Primary Contact (Property Manager)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-64">
          <div className="md:col-span-2" data-id="element-65">
            <label className={labelClasses} data-id="element-66">Contact Name</label>
            <input type="text" name="primaryName" value={formData.primaryName} onChange={handleChange} className={inputClasses} data-id="element-67" />
          </div>
          <div data-id="element-68">
            <label className={labelClasses} data-id="element-69">Phone Number</label>
            <input type="tel" name="primaryPhone" value={formData.primaryPhone} onChange={handleChange} className={inputClasses} placeholder="(555) 000-0000" data-id="element-70" />
          </div>
          <div data-id="element-71">
            <label className={labelClasses} data-id="element-72">Email Address</label>
            <input type="email" name="primaryEmail" value={formData.primaryEmail} onChange={handleChange} className={inputClasses} placeholder="manager@property.com" data-id="element-73" />
          </div>
        </div>

        {/* Section C */}
        <h3 className={sectionHeaderClasses} data-id="element-74">
          Secondary Contact (Maintenance Manager)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-75">
          <div data-id="element-76">
            <label className={labelClasses} data-id="element-77">Contact Name</label>
            <input type="text" name="secondaryName" value={formData.secondaryName} onChange={handleChange} className={inputClasses} data-id="element-78" />
          </div>
          <div data-id="element-79">
            <label className={labelClasses} data-id="element-80">Phone Number</label>
            <input type="tel" name="secondaryPhone" value={formData.secondaryPhone} onChange={handleChange} className={inputClasses} data-id="element-81" />
          </div>
        </div>

        {/* Section D */}
        <h3 className={sectionHeaderClasses} data-id="element-82">Service Details</h3>
        <div className="space-y-6 mb-6" data-id="element-83">
          <div data-id="element-84">
            <label className={labelClasses} data-id="element-85">Service Type Needed</label>
            <select name="serviceType" value={formData.serviceType} onChange={handleChange} className={inputClasses} data-id="element-86">
              <option value="" data-id="element-87">Select a service type...</option>
              {SERVICE_TYPES.map(type => <option key={type} value={type} data-id="element-88">
                  {type}
                </option>)}
            </select>
          </div>
          <div data-id="element-89">
            <label className={labelClasses} data-id="element-90">
              Description of Needs/Issue <span className="text-tcp-red" data-id="element-91">*</span>
            </label>
            <textarea name="description" value={formData.description} onChange={handleChange} required rows={5} className={`${inputClasses} resize-none`} placeholder="Please describe your project or maintenance needs in detail..." data-id="element-92" />
          </div>
        </div>

        {/* Section E */}
        <h3 className={sectionHeaderClasses} data-id="element-93">File Upload</h3>
        <div className="mb-8" data-id="element-94">
          <label className={labelClasses} data-id="element-95">Upload Documents/Photos</label>
          <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-white/20 rounded-sm p-8 text-center cursor-pointer hover:border-tcp-red/50 hover:bg-white/[0.02] transition-colors" data-id="element-96">
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*,.pdf" className="hidden" data-id="element-97" />
            {fileName ? <div className="flex flex-col items-center text-tcp-red" data-id="element-98">
                <CheckCircle2Icon className="w-8 h-8 mb-2" data-id="element-99" />
                <span className="font-medium text-white" data-id="element-100">{fileName}</span>
                <span className="text-xs text-tcp-gray mt-1" data-id="element-101">
                  Click to change file
                </span>
              </div> : <div className="flex flex-col items-center text-tcp-gray" data-id="element-102">
                <UploadIcon className="w-8 h-8 mb-3 text-white/50" data-id="element-103" />
                <span className="text-white font-medium mb-1" data-id="element-104">
                  Click to upload or drag and drop
                </span>
                <span className="text-xs" data-id="element-105">Accepted formats: JPG, PNG, PDF</span>
                <span className="text-xs mt-1 text-tcp-red/80" data-id="element-106">
                  Maximum file size: 2MB
                </span>
              </div>}
          </div>
        </div>

        {/* Section F */}
        <div className="bg-[#111111] p-4 rounded-sm border border-white/5 mb-8 text-left" data-id="element-107">
          <div className="flex items-start gap-3" data-id="element-108">
            <label className="relative flex items-center pt-0.5 cursor-pointer shrink-0" data-id="element-109">
              <input type="checkbox" name="agreement" checked={formData.agreement} onChange={handleChange} required className="peer sr-only" data-id="element-110" />
              <div className="w-5 h-5 border-2 border-white/30 rounded-sm peer-checked:bg-tcp-red peer-checked:border-tcp-red transition-colors flex items-center justify-center" data-id="element-111">
                <CheckCircle2Icon className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100" strokeWidth={3} data-id="element-112" />
              </div>
            </label>
            <p className="text-tcp-gray text-left text-[12px]" data-id="element-113">
              I have read the{' '}
              <button type="button" onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setWorksheetOpen(true);
            }} className="text-tcp-red underline underline-offset-2 hover:text-white transition-colors font-medium text-left" data-id="element-114">
                Customer Awareness Worksheet
              </button>{' '}
              and acknowledge that I could be charged a service fee.
            </p>
          </div>
        </div>

        <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-tcp-red text-white font-medium rounded-sm hover:bg-tcp-darkred transition-all shadow-[0_0_15px_rgba(183,29,35,0.2)] hover:shadow-[0_0_25px_rgba(183,29,35,0.4)] disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center gap-2" data-id="element-115">
          {isSubmitting ? <>
              <Loader2Icon className="w-5 h-5 animate-spin" data-id="element-116" />
              Processing...
            </> : submitButtonText}
        </button>
      </motion.form>

      {/* Customer Awareness Worksheet Modal */}
      <AnimatePresence data-id="element-117">
        {worksheetOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setWorksheetOpen(false)} data-id="element-118">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 10
        }} transition={{
          duration: 0.2
        }} onClick={e => e.stopPropagation()} className="bg-tcp-darkgray border border-white/10 rounded-md w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl" data-id="element-119">
              <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-120">
                <h3 className="font-serif text-xl font-bold text-white" data-id="element-121">
                  Customer Awareness Worksheet
                </h3>
                <button type="button" onClick={() => setWorksheetOpen(false)} className="text-tcp-gray hover:text-white transition-colors p-1" data-id="element-122">
                  <XIcon className="w-5 h-5" data-id="element-123" />
                </button>
              </div>
              <div className="p-6 space-y-4 text-sm text-tcp-gray leading-relaxed" data-id="element-124">
                <p data-id="element-125">
                  By submitting this maintenance or service request, you
                  acknowledge and agree to the following terms:
                </p>
                <ul className="list-disc pl-5 space-y-3" data-id="element-126">
                  <li data-id="element-127">
                    <strong className="text-white" data-id="element-128">Service Fee:</strong> A
                    service fee may apply for all maintenance visits,
                    inspections, and repair work performed by The Christmas
                    Palace. Fees vary based on the scope of work, travel
                    distance, and materials required.
                  </li>
                  <li data-id="element-129">
                    <strong className="text-white" data-id="element-130">Emergency Requests:</strong>{' '}
                    Requests submitted outside of normal business hours or
                    requiring expedited response may incur additional charges.
                  </li>
                  <li data-id="element-131">
                    <strong className="text-white" data-id="element-132">Cancellation Policy:</strong>{' '}
                    Cancellations made less than 24 hours before a scheduled
                    service visit may be subject to a cancellation fee.
                  </li>
                  <li data-id="element-133">
                    <strong className="text-white" data-id="element-134">Property Access:</strong> You
                    are responsible for ensuring that our team has safe and
                    adequate access to the property and all areas requiring
                    service.
                  </li>
                  <li data-id="element-135">
                    <strong className="text-white" data-id="element-136">Liability:</strong> The
                    Christmas Palace is not responsible for pre-existing damage
                    to property, structures, or décor not installed by our team.
                  </li>
                  <li data-id="element-137">
                    <strong className="text-white" data-id="element-138">Estimates:</strong> Any cost
                    estimates provided are subject to change upon on-site
                    inspection. Final charges will be confirmed before work
                    begins.
                  </li>
                </ul>
                <p className="pt-2 text-white/70" data-id="element-139">
                  If you have any questions about these terms, please contact
                  our commercial team before submitting your request.
                </p>
              </div>
              <div className="p-6 border-t border-white/10" data-id="element-140">
                <button type="button" onClick={() => setWorksheetOpen(false)} className="w-full py-3 bg-tcp-red text-white font-medium rounded-sm hover:bg-tcp-darkred transition-all" data-id="element-141">
                  I Understand
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}